import { gql } from "@apollo/client";
import { composeResolvers } from "@graphql-tools/resolvers-composition";
import { isAuthenticated, logicaErrorBody } from "./helper";
import ChatGuideLineAgreement from "@/lib/chatGuideLineAgreement";
import { GraphQLError } from "graphql";

export const typeDef = gql`
  type ChatGuidelineAgreement @cacheControl(scope: PRIVATE, maxAge: 0) {
    agreement: Boolean
  }

  type ChatRoomConfig {
    messageLimit: Int
    pollingInterval: Int
    initialMessageLimit: Int
    userLimit: Int
  }

  type ChatRoomCustomData {
    type: String
    liveStartBefore: Int
    liveEndAfter: Int
    skipMessageThreshold: Int
    appMessageLimit: Int
    chatEndDateTime: String
  }

  type ChatRoom {
    roomId: String
    config: ChatRoomConfig
    customData: ChatRoomCustomData
  }

  type ChatRooms @cacheControl(scope: PRIVATE) {
    totalCount: Int
    rooms: [ChatRoom]
  }

  type Token @cacheControl(scope: PRIVATE) {
    token: String
  }

  type ChatUser {
    roomId: String
    refId: String
    id: String
  }

  type Sender {
    displayName: String
    refId: String
    id: String
  }

  type ChatMessage {
    message: String
    sender: Sender
    roomId: String
    isNGMessage: Boolean
    id: String
  }

  type MessageResult {
    result: Boolean
  }

  type Query {
    getChatGuideLineAgreement: ChatGuidelineAgreement
    getRooms(metaId: Int): ChatRooms
    getRoomToken(roomId: String): Token
    getUserToken(roomId: String, userId: String): Token
  }

  type Mutation {
    setChatGuideLineAgreement: ChatGuidelineAgreement
    createUser(roomId: String, metaId: Int): ChatUser
    postChatMessage(
      roomId: String
      userId: String
      message: String
      refId: String
      displayName: String
      token: String
    ): ChatMessage
    deleteChatMessage(roomId: String, messageId: String): MessageResult
    reportChatMessage(
      roomId: String
      messageId: String
      reason: String
    ): MessageResult
  }
`;

export const resolvers = composeResolvers(
  {
    Query: {
      getChatGuideLineAgreement: async (
        _parent,
        {},
        { req, res, dataSources }
      ) => {
        try {
          console.log("get cookie");
          const agreement = new ChatGuideLineAgreement(req, res);
          const cookie = await agreement.getCookie();
          console.log(cookie);
          return {
            agreement: !!cookie,
            nickName: cookie,
          };
        } catch (e) {
          console.log("getChatGuideLineAgreement error", e);
          // console.log(JSON.stringify(e.extentions, null, 2));
        }
      },
      getRooms: async (_parent, { metaId }, { dataSources }) => {
        try {
          const res = await dataSources.meta.getRooms(metaId);
          console.log("res.meta_rooms", res.meta_rooms);
          // Fisher-Yatesアルゴリズム
          const shuffleArray = (arr) => {
            var i, j, temp;
            arr = arr.slice();
            i = arr.length;
            if (i === 0) {
              return arr;
            }
            while (--i) {
              j = Math.floor(Math.random() * (i + 1));
              temp = arr[i];
              arr[i] = arr[j];
              arr[j] = temp;
            }
            return arr;
          };
          const shuffledRooms = shuffleArray(res.meta_rooms);
          console.log("shuffledRooms", shuffledRooms);
          const rooms = shuffledRooms.map((room) => {
            const config = {
              messageLimit: room?.config?.messageLimit,
              pollingInterval: room?.config?.pollingInterval,
              initialMessageLimit: room?.config?.initialMessageLimit,
              userLimit: room?.config?.userLimit,
            };
            const customData = {
              type: room?.custom_data?.type,
              liveStartBefore: room?.custom_data?.liveStartBefore,
              liveEndAfter: room?.custom_data?.liveEndAfter,
              skipMessageThreshold: room?.custom_data?.skipMessageThreshold,
              appMessageLimit: room?.custom_data?.appMessageLimit,
              chatEndDateTime: room?.custom_data?.chatEndDateTime,
            };

            return {
              roomId: room.room_id,
              config: config,
              customData: customData,
            };
          });
          return {
            totalCount: res.total_count,
            rooms: rooms,
          };
        } catch (e) {
          console.log("getRooms error:", e);
        }
      },
      getRoomToken: async (_parent, { roomId }, { dataSources }) => {
        try {
          const res = await dataSources.chat.getRoomToken(roomId);
          return {
            token: res.token,
          };
        } catch (e) {
          console.log("getRoomToken error:", e);
        }
      },
      getUserToken: async (_parent, { roomId, userId }, { dataSources }) => {
        try {
          const res = await dataSources.chat.getUserToken(roomId, userId);
          return res;
        } catch (e) {
          console.log("getUserToken error:", e);
        }
      },
    },
    Mutation: {
      setChatGuideLineAgreement: async (
        _parent,
        {},
        { req, res, dataSources }
      ) => {
        try {
          const agreement = new ChatGuideLineAgreement(req, res);
          console.log("set cookie");
          await agreement.setCookie(true);
          return {
            agreement: true,
          };
        } catch (e) {
          console.log("setChatGuideLineAgreement error", e);
          // console.log(JSON.stringify(e.extentions, null, 2));
        }
      },
      createUser: async (_parent, { roomId, metaId }, { dataSources }) => {
        try {
          const res = await dataSources.chat.createUser(roomId, metaId);
          const user = res.user;
          return {
            roomId: user.roomId,
            refId: user.refId,
            id: user.id,
          };
        } catch (e) {
          console.log("createUser error:", e);
          const body = logicaErrorBody(e);
          const errorCode = body?.error?.code;
          if (errorCode) {
            throw new GraphQLError(e.message, {
              extensions: { error: { code: errorCode } },
            });
          }
        }
      },
      postChatMessage: async (
        _parent,
        { roomId, userId, message, refId, displayName, token },
        { dataSources }
      ) => {
        try {
          const res = await dataSources.chat.postMessage(
            roomId,
            userId,
            message,
            refId,
            displayName,
            token
          );
          const msg = res.message;
          return {
            message: msg.data.message,
            sender: msg.data.sender,
            roomId: msg.data.roomId,
            isNGMessage: msg.data.isNGMessage,
            id: msg.data.id,
          };
        } catch (e) {
          console.log("postChatMessage error:", e);
          const body = logicaErrorBody(e);
          const errorCode = body?.error?.code;
          if (errorCode) {
            throw new GraphQLError(e.message, {
              extensions: { error: { code: errorCode } },
            });
          }
        }
      },
      deleteChatMessage: async (
        _parent,
        { roomId, messageId },
        { dataSources }
      ) => {
        try {
          const res = await dataSources.chat.deleteMessage(roomId, messageId);
          return {
            result: res.result,
          };
        } catch (e) {
          console.log("deleteChatMessage error:", e);
          const body = logicaErrorBody(e);
          const errorCode = body?.error?.code;
          if (errorCode) {
            throw new GraphQLError(e.message, {
              extensions: { error: { code: errorCode } },
            });
          }
        }
      },
      reportChatMessage: async (
        _parent,
        { roomId, messageId, reason },
        { dataSources }
      ) => {
        try {
          const res = await dataSources.chat.reportMessage(
            roomId,
            messageId,
            reason
          );
          return {
            result: res.result,
          };
        } catch (e) {
          console.log("reportChatMessage error:", e);
          const body = logicaErrorBody(e);
          const errorCode = body?.error?.code;
          if (errorCode) {
            throw new GraphQLError(e.message, {
              extensions: { error: { code: errorCode } },
            });
          }
        }
      },
    },
  },
  {
    "Query.getChatGuideLineAgreement": [isAuthenticated()],
    "Query.getRooms": [isAuthenticated()],
    "Query.getRoomToken": [isAuthenticated()],
    "Query.getUserToken": [isAuthenticated()],
    "Mutation.setChatGuideLineAgreement": [isAuthenticated()],
    "Mutation.createUser": [isAuthenticated()],
    "Mutation.postChatMessage": [isAuthenticated()],
    "Mutation.deleteChatMessage": [isAuthenticated()],
    "Mutation.reportChatMessage": [isAuthenticated()],
  }
);
